import * as React from "react";
import { Search } from "lucide-react";
import { cn, FOCUS_RING } from "~/lib/utils";
import { Kbd } from "~/components/ui/kbd";

export interface AppTopBarProps {
  /** Left slot: breadcrumb, workspace selector, or data-mode indicator. */
  left?: React.ReactNode;
  /** Centre slot: search affordance (default) or custom content. */
  center?: React.ReactNode;
  /** Right slot: activity, help, theme, account menu. */
  right?: React.ReactNode;
}

/**
 * Top application bar. Presentational component that renders only the slots
 * supplied. Left: breadcrumb/workspace + data-mode indicator. Centre: search
 * field with ⌘K hint (default). Right: activity, help, theme, account.
 *
 * Every slot is optional and renders nothing if absent.
 */
export function AppTopBar({ left, center, right }: AppTopBarProps): React.ReactElement {
  const hasDefaultSearch = center === undefined;

  return (
    <header
      data-slot="app-top-bar"
      className={cn(
        "flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background px-3",
        "min-w-0",
      )}
    >
      {/* Left cluster: workspace/breadcrumb + data-mode */}
      {left && <div className={cn("flex items-center gap-2 min-w-0")}>{left}</div>}

      {/* Centre cluster: search or custom */}
      <div className="flex flex-1 items-center justify-center min-w-0">
        {hasDefaultSearch ? (
          <button
            type="button"
            className={cn(
              "group relative flex h-8 w-full items-center gap-2 rounded border border-border-strong bg-surface-1 px-2.5 text-sm text-foreground-muted transition-colors hover:bg-surface-2 focus:outline-none",
              FOCUS_RING,
            )}
            aria-label="Search mail and entities"
          >
            {/* Icon always visible */}
            <Search className="h-4 w-4 shrink-0" aria-hidden="true" />

            {/* Text hidden on narrow screens */}
            <span className="hidden text-foreground-muted sm:inline">Search or jump to…</span>

            {/* ⌘K hint on desktop, hidden on narrow */}
            <Kbd className="ml-auto hidden sm:inline-flex">⌘K</Kbd>
          </button>
        ) : (
          center
        )}
      </div>

      {/* Right cluster: activity, help, theme, account */}
      {right && (
        <div data-slot="app-top-bar-right" className={cn("flex items-center gap-2 shrink-0")}>
          {right}
        </div>
      )}
    </header>
  );
}
